import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { useOrganization } from "@/auth/organization";
import { Notice } from "@/components/domains/parts";
import {
  Badge,
  Button,
  ErrorAlert,
  ErrorState,
  Field,
  Form,
  Input,
  Modal,
  Section,
  Spinner,
  type Requirement,
} from "@/components/ui";
import {
  CHANGE_LABEL,
  MAILBOX_PILL,
  PENDING_LABEL,
  emailApi,
  emailKeys,
  useMailboxes,
  type EmailDomain,
  type EmailItem,
  type Mailbox,
  type MailboxActionName,
  type MailboxChange,
  type MailboxList,
} from "@/lib/email";
import { formatDate, formatDateTime, timeAgo } from "@/lib/format";

/** A change the person is asked to confirm first (it stops mail, or it can't be undone without a recovery). */
type Confirmable = "suspend" | "reset_password" | "delete";

type Dialog =
  | { kind: "add" }
  | { kind: "rename"; box: Mailbox }
  | { kind: "confirm"; box: Mailbox; action: Confirmable }
  | { kind: "password"; address: string; password: string; reset: boolean }
  | null;

/** The latest change that did not finish, kept beside the list until the next one (its reference is what support asks for). */
interface Unfinished {
  address: string;
  outcome: MailboxChange["outcome"];
  message: string | null;
  reference: string | null;
}

/**
 * The mailboxes on one connected domain (PHP-3304): add, rename, suspend, resume, reset the password, delete and
 * recover, with each one's aliases and forwarding. The server decides what may be done; a button it says no to is
 * disabled with its reason. A change the provider has not confirmed shows its reference and Retry, and the state shown
 * is always the last one the provider confirmed.
 */
export function Mailboxes({ row, onAnnounce }: { row: EmailDomain; onAnnounce: (message: string) => void }) {
  const { orgId } = useOrganization();
  const list = useMailboxes(row.id);
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [unfinished, setUnfinished] = useState<Unfinished | null>(null);
  const manage: Requirement = { permission: "email:manage", on: ["domain_folder", row.folder_id] };

  const change = useMutation({
    mutationFn: (input: { address: string; label: string; run: () => Promise<MailboxChange> }) => input.run(),
    onSuccess: async (result, input) => {
      await qc.invalidateQueries({ queryKey: emailKeys.all(orgId) });
      setDialog(
        result.one_time_password
          ? {
              kind: "password",
              address: result.mailbox?.address ?? input.address,
              password: result.one_time_password,
              reset: input.label === CHANGE_LABEL.reset_password,
            }
          : null,
      );
      if (result.outcome === "done") {
        setUnfinished(null);
        onAnnounce(
          `${input.address}: ${input.label.toLowerCase()} done.${result.message ? ` ${result.message}` : ""}`,
        );
      } else {
        setUnfinished({
          address: input.address,
          outcome: result.outcome,
          message: result.message,
          reference: result.operation_reference,
        });
        onAnnounce(`${input.address}: ${result.message ?? "the change is not finished yet."}`);
      }
    },
  });

  const run = (address: string, label: string, call: () => Promise<MailboxChange>) =>
    change.mutate({ address, label, run: call });

  if (list.isPending) return <Spinner />;
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const data = list.data;
  // The full reason and what to do are in the notice above the section; the button says the short version.
  const blocked = data.provisioning.allowed
    ? undefined
    : (data.provisioning.message ?? "Mailboxes can't be added yet.");

  return (
    <Section
      title="Mailboxes"
      subtitle={`${data.usage.used} of ${data.usage.limit} mailboxes in your plan are in use`}
      action={
        <Button requires={manage} disabledReason={blocked} onClick={() => setDialog({ kind: "add" })}>
          Add mailbox
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {unfinished && <UnfinishedNotice unfinished={unfinished} />}
        {dialog?.kind !== "confirm" && dialog?.kind !== "rename" && dialog?.kind !== "add" && (
          <ErrorAlert error={change.error} />
        )}
        {data.mailboxes.length === 0 ? (
          <p className="text-sm text-fg-muted">
            No mailboxes on {row.fqdn} yet.
            {data.provisioning.allowed ? " Add the first one." : ""}
          </p>
        ) : (
          <ul className="flex flex-col gap-3" aria-label={`Mailboxes on ${row.fqdn}`}>
            {data.mailboxes.map((box) => (
              <MailboxCard
                key={box.id}
                box={box}
                list={data}
                manage={manage}
                busy={change.isPending}
                onConfirm={(action) => setDialog({ kind: "confirm", box, action })}
                onRename={() => setDialog({ kind: "rename", box })}
                onRun={(label, call) => run(box.address, label, call)}
              />
            ))}
          </ul>
        )}
      </div>

      {dialog?.kind === "add" && (
        <AddMailboxModal
          list={data}
          submitting={change.isPending}
          error={change.error}
          onClose={() => {
            change.reset();
            setDialog(null);
          }}
          onSubmit={(local, name) =>
            run(`${local.trim().toLowerCase()}@${data.fqdn}`, CHANGE_LABEL.create!, () =>
              emailApi.createMailbox(orgId, row.id, { local_part: local, display_name: name }),
            )
          }
        />
      )}
      {dialog?.kind === "rename" && (
        <RenameModal
          box={dialog.box}
          submitting={change.isPending}
          error={change.error}
          onClose={() => {
            change.reset();
            setDialog(null);
          }}
          onSubmit={(name) =>
            run(dialog.box.address, CHANGE_LABEL.update!, () =>
              emailApi.renameMailbox(orgId, dialog.box.id, name),
            )
          }
        />
      )}
      {dialog?.kind === "confirm" && (
        <ConfirmModal
          box={dialog.box}
          action={dialog.action}
          recoveryDays={data.recovery_days}
          submitting={change.isPending}
          error={change.error}
          onClose={() => {
            change.reset();
            setDialog(null);
          }}
          onConfirm={() => {
            const { box, action } = dialog;
            run(box.address, CHANGE_LABEL[action]!, () =>
              action === "delete"
                ? emailApi.deleteMailbox(orgId, box.id, true)
                : emailApi.command(orgId, box.id, action === "suspend" ? "suspend" : "reset-password"),
            );
          }}
        />
      )}
      {dialog?.kind === "password" && (
        <PasswordModal
          address={dialog.address}
          password={dialog.password}
          reset={dialog.reset}
          onClose={() => setDialog(null)}
        />
      )}
    </Section>
  );
}

function UnfinishedNotice({ unfinished }: { unfinished: Unfinished }) {
  return (
    <Notice
      tone={unfinished.outcome === "in_progress" ? "processing" : "warning"}
      title={`${unfinished.address}: ${PENDING_LABEL[unfinished.outcome === "done" ? "in_progress" : unfinished.outcome]}`}
    >
      {unfinished.message}
      {unfinished.reference && (
        <>
          {" "}
          Reference <span className="font-mono">{unfinished.reference}</span>.
        </>
      )}
    </Notice>
  );
}

function reasonOf(box: Mailbox, action: MailboxActionName): string | undefined {
  const a = box.actions[action];
  return a.allowed ? undefined : (a.reason ?? "Not available now.");
}

function MailboxCard({
  box,
  list,
  manage,
  busy,
  onConfirm,
  onRename,
  onRun,
}: {
  box: Mailbox;
  list: MailboxList;
  manage: Requirement;
  busy: boolean;
  onConfirm: (action: Confirmable) => void;
  onRename: () => void;
  onRun: (label: string, call: () => Promise<MailboxChange>) => void;
}) {
  const { orgId } = useOrganization();
  const pill = MAILBOX_PILL[box.state];
  const headingId = `mailbox-${box.id}`;
  return (
    <li aria-labelledby={headingId} className="rounded-xl border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={headingId} className="font-semibold break-all text-fg">
            {box.address}
          </h3>
          <p className="text-sm text-fg-muted">{box.display_name}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-subtle">
            <Badge tone={pill.tone}>{pill.label}</Badge>
            {box.last_confirmed_at && (
              <span>
                Confirmed <time dateTime={box.last_confirmed_at}>{timeAgo(box.last_confirmed_at)}</time> (
                {formatDateTime(box.last_confirmed_at)})
              </span>
            )}
            {box.state === "deleted" && box.purge_after && (
              <span>Can be recovered until {formatDate(box.purge_after)}</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {box.state === "deleted" ? (
            <Button
              size="sm"
              variant="secondary"
              requires={manage}
              disabledReason={reasonOf(box, "recover")}
              loading={busy}
              onClick={() => onRun(CHANGE_LABEL.recover!, () => emailApi.command(orgId, box.id, "recover"))}
            >
              Recover
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="secondary"
                requires={manage}
                disabledReason={reasonOf(box, "update")}
                onClick={onRename}
              >
                Rename
              </Button>
              {box.state === "suspended" ? (
                <Button
                  size="sm"
                  variant="secondary"
                  requires={manage}
                  disabledReason={reasonOf(box, "resume")}
                  loading={busy}
                  onClick={() => onRun(CHANGE_LABEL.resume!, () => emailApi.command(orgId, box.id, "resume"))}
                >
                  Resume
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  requires={manage}
                  disabledReason={reasonOf(box, "suspend")}
                  onClick={() => onConfirm("suspend")}
                >
                  Suspend
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                requires={manage}
                disabledReason={reasonOf(box, "reset_password")}
                onClick={() => onConfirm("reset_password")}
              >
                Reset password
              </Button>
              <Button
                size="sm"
                variant="dangerOutline"
                requires={manage}
                disabledReason={reasonOf(box, "delete")}
                onClick={() => onConfirm("delete")}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {box.pending && (
        <div className="mt-3">
          <Notice
            tone={box.pending.outcome === "in_progress" ? "processing" : "warning"}
            title={`${PENDING_LABEL[box.pending.outcome]}: ${(CHANGE_LABEL[box.pending.action] ?? box.pending.action).toLowerCase()}`}
            actions={
              <Button
                size="sm"
                variant="secondary"
                requires={manage}
                disabledReason={reasonOf(box, "retry")}
                loading={busy}
                onClick={() => onRun("Retry", () => emailApi.command(orgId, box.id, "retry"))}
              >
                Retry
              </Button>
            }
          >
            {box.pending.message} The mailbox stays as last confirmed until this is settled.
            {box.pending.reference && (
              <>
                {" "}
                Reference <span className="font-mono">{box.pending.reference}</span>. Quote it if you contact
                support.
              </>
            )}
          </Notice>
        </div>
      )}

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <ItemsPanel
          box={box}
          kind="alias"
          items={box.aliases}
          limit={list.limits.aliases_per_mailbox}
          manage={manage}
          busy={busy}
          onRun={onRun}
        />
        <ItemsPanel
          box={box}
          kind="forwarding"
          items={box.forwardings}
          limit={list.limits.forwardings_per_mailbox}
          manage={manage}
          busy={busy}
          onRun={onRun}
        />
      </div>
    </li>
  );
}

const ITEM_TEXT = {
  alias: {
    title: "Aliases",
    empty: "No aliases. An alias is another address that delivers here.",
    label: "New alias",
    add: "Add alias",
    action: "add_alias" as const,
  },
  forwarding: {
    title: "Forwarding",
    empty: "Mail isn't forwarded anywhere.",
    label: "Forward to",
    add: "Add forwarding",
    action: "add_forwarding" as const,
  },
};

function ItemsPanel({
  box,
  kind,
  items,
  limit,
  manage,
  busy,
  onRun,
}: {
  box: Mailbox;
  kind: "alias" | "forwarding";
  items: EmailItem[];
  limit: number;
  manage: Requirement;
  busy: boolean;
  onRun: (label: string, call: () => Promise<MailboxChange>) => void;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const text = ITEM_TEXT[kind];
  const [value, setValue] = useState("");
  const fqdn = box.address.split("@")[1] ?? "";
  const add = useMutation({
    mutationFn: () =>
      kind === "alias"
        ? emailApi.addAlias(orgId, box.id, value.trim())
        : emailApi.addForwarding(orgId, box.id, value.trim()),
    onSuccess: async () => {
      setValue("");
      await qc.invalidateQueries({ queryKey: emailKeys.all(orgId) });
    },
  });
  const formId = `${kind}-form-${box.id}`;
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-semibold text-fg">
        {text.title}{" "}
        <span className="font-normal text-fg-subtle">
          ({items.length} of {limit})
        </span>
      </h4>
      {items.length === 0 ? (
        <p className="text-sm text-fg-muted">{text.empty}</p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center justify-between gap-2">
              <span className="break-all">
                {item.address}
                {item.pending && (
                  <span className="ml-2 text-xs text-fg-subtle">
                    {PENDING_LABEL[item.pending.outcome]}
                    {item.pending.reference ? ` (${item.pending.reference})` : ""}
                  </span>
                )}
              </span>
              <Button
                size="sm"
                variant="ghost"
                requires={manage}
                disabledReason={item.remove_reason ?? undefined}
                loading={busy}
                aria-label={`Remove ${item.address}`}
                onClick={() =>
                  onRun(`Remove ${item.address}`, () => emailApi.removeItem(orgId, box.id, item.id))
                }
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      {box.state !== "deleted" && (
        <Form
          id={formId}
          error={add.error}
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <div className="min-w-[200px] flex-1">
            <Field
              label={text.label}
              name={kind === "alias" ? "local_part" : "target"}
              required
              hint={kind === "alias" ? `Ends in @${fqdn}` : "Any e-mail address"}
            >
              {(props) => (
                <Input
                  {...props}
                  type={kind === "alias" ? "text" : "email"}
                  maxLength={kind === "alias" ? 64 : 254}
                  value={value}
                  autoComplete="off"
                  onChange={(e) => setValue(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Button
            type="submit"
            variant="secondary"
            requires={manage}
            disabledReason={reasonOf(box, text.action)}
            loading={add.isPending}
          >
            {text.add}
          </Button>
        </Form>
      )}
    </div>
  );
}

function AddMailboxModal({
  list,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  list: MailboxList;
  submitting: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (local: string, name: string) => void;
}) {
  const [local, setLocal] = useState("");
  const [name, setName] = useState("");
  return (
    <Modal
      open
      onClose={onClose}
      title="Add mailbox"
      subtitle={`${list.usage.used} of ${list.usage.limit} mailboxes in use`}
      note="You'll see a temporary password once. The owner changes it at their first sign-in."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="add-mailbox-form" loading={submitting}>
            Create mailbox
          </Button>
        </>
      }
    >
      <Form
        id="add-mailbox-form"
        error={error}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(local, name);
        }}
      >
        <Field
          label="Address"
          name="local_part"
          required
          hint={`Letters, numbers, dots, hyphens or underscores. It ends in @${list.fqdn}.`}
        >
          {(props) => (
            <div className="flex items-center gap-2">
              <Input
                {...props}
                value={local}
                maxLength={64}
                autoComplete="off"
                onChange={(e) => setLocal(e.target.value)}
              />
              <span className="shrink-0 text-sm text-fg-muted">@{list.fqdn}</span>
            </div>
          )}
        </Field>
        <Field
          label="Name people see"
          name="display_name"
          required
          hint="Shown as the sender, like Sales Team."
        >
          {(props) => (
            <Input {...props} value={name} maxLength={64} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>
      </Form>
    </Modal>
  );
}

function RenameModal({
  box,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  box: Mailbox;
  submitting: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(box.display_name);
  return (
    <Modal
      open
      onClose={onClose}
      title="Rename mailbox"
      subtitle={box.address}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="rename-mailbox-form" loading={submitting}>
            Save name
          </Button>
        </>
      }
    >
      <Form
        id="rename-mailbox-form"
        error={error}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(name);
        }}
      >
        <Field label="Name people see" name="display_name" required>
          {(props) => (
            <Input {...props} value={name} maxLength={64} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>
      </Form>
    </Modal>
  );
}

const CONFIRM_TEXT: Record<Confirmable, { title: string; button: string }> = {
  suspend: { title: "Suspend this mailbox?", button: "Suspend mailbox" },
  reset_password: { title: "Reset the password?", button: "Reset password" },
  delete: { title: "Delete this mailbox?", button: "Delete mailbox" },
};

function ConfirmModal({
  box,
  action,
  recoveryDays,
  submitting,
  error,
  onClose,
  onConfirm,
}: {
  box: Mailbox;
  action: Confirmable;
  recoveryDays: number;
  submitting: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const text = CONFIRM_TEXT[action];
  return (
    <Modal
      open
      onClose={onClose}
      title={text.title}
      subtitle={box.address}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={action === "delete" ? "danger" : "primary"}
            loading={submitting}
            onClick={onConfirm}
          >
            {text.button}
          </Button>
        </>
      }
    >
      {action === "suspend" && (
        <p className="text-sm">
          Nobody can sign in to it and it stops sending and receiving mail. Nothing is deleted, and you can
          resume it at any time.
        </p>
      )}
      {action === "reset_password" && (
        <p className="text-sm">
          The current password stops working. You'll see a temporary password once; give it to the mailbox's
          owner, who chooses a new one at their next sign-in. The mailbox and the people who manage email are
          told.
        </p>
      )}
      {action === "delete" && (
        <div className="flex flex-col gap-2 text-sm">
          <p>
            It stops working straight away. You can recover it, with its mail, for {recoveryDays} days; after
            that it is deleted for good.
          </p>
          {box.delete_consequences.length > 0 && (
            <>
              <p className="font-semibold">This also affects:</p>
              <ul className="list-disc pl-5">
                {box.delete_consequences.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      <ErrorAlert error={error} />
    </Modal>
  );
}

function PasswordModal({
  address,
  password,
  reset,
  onClose,
}: {
  address: string;
  password: string;
  reset: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<"yes" | "no" | null>(null);
  const copy = async () => {
    try {
      if (!navigator.clipboard) throw new Error("no clipboard");
      await navigator.clipboard.writeText(password);
      setCopied("yes");
    } catch {
      setCopied("no");
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={reset ? "New temporary password" : "Mailbox created"}
      subtitle={address}
      footer={<Button onClick={onClose}>I've saved it</Button>}
    >
      <p className="text-sm">
        This is the only time this password is shown. We don't keep it. Give it to the mailbox's owner: they
        choose a new one at their first sign-in.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <code
          aria-label="Temporary password"
          className="rounded bg-muted px-2 py-1 font-mono text-base break-all select-all"
        >
          {password}
        </code>
        <Button size="sm" variant="secondary" onClick={() => void copy()}>
          Copy
        </Button>
      </div>
      <p role="status" className="text-xs text-fg-subtle">
        {copied === "yes"
          ? "Copied."
          : copied === "no"
            ? "Couldn't copy. Select the password and copy it."
            : ""}
      </p>
    </Modal>
  );
}
